import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  styled,
  alpha,
  InputBase,
  CircularProgress
} from '@mui/material';
import CustomPagination from 'components/pagination';
import SearchIcon from '@mui/icons-material/Search';
import { Http } from 'api/http/http';
import { logout } from 'store/reducers/auth';
import { openStudentInfoInNewTab } from 'utils/studentInfoUtils';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25)
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(0),
    width: 'auto'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch'
      }
    }
  }
}));

const columns = [
  { id: 'studentNumber', label: '학번', minWidth: 100, align: 'center' },
  { id: 'studentName', label: '이름', width: 70, align: 'center' },
  { id: 'departmentName', label: '학과', minWidth: 100, align: 'center' },
  { id: 'scholarshipIssuedNumber', label: '발급번호', minWidth: 100, align: 'center' },
  { id: 'scholarshipIssuedYear', label: '년도', width: 65, align: 'center' },
  { id: 'scholarshipIssuedSemester', label: '학기', width: 50, align: 'center' },
  { id: 'level', label: '과정', width: 70, align: 'center' },
  { id: 'moduleName', label: '모듈명', width: 80, align: 'center' },
  { id: 'amount', label: '장학금액', minWidth: 100, align: 'center' },
  { id: 'totalGrade', label: '총성적', minWidth: 100, align: 'center' },
  { id: 'pdf', label: '다운로드', minWidth: 100, align: 'center' }
];

// 정렬기능을 이용하기 위해 api 문서의 필드와 컬럼 필드명이랑 매핑시킨 객체
const apiFieldMapping = {
  studentNumber: 'm.studentNumber',
  studentName: 'm.memberName',
  departmentName: 'm.departmentName',
  scholarshipNumber: 'scholarshipNumber',
  year: 'year',
  semester: 'semester',
  process: 'md.process',
  moduleName: 'md.moduleName',
  amount: 'amountGrade',
  totalGrade: 'totalGrade'
};

export default function AdminScholarship() {
  const rowsPerPage = 20;
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true); // 추가

  // 기본 정렬 상태 설정
  const [sortConfig, setSortConfig] = useState({ field: 'year', direction: 'desc' });

  const [moduleFilterSelected, setModuleFilterSelected] = useState('');
  const [yearFilterSelected, setYearFilterSelected] = useState('');
  const [amountFilterSelected, setAmountFilterSelected] = useState('');
  const [semesterFilterSelected, setSemesterFilterSelected] = useState('');

  // 모듈 학기를 상수로 선언
  const semesterOptions = [
    { label: '1학기', value: '1' },
    { label: '2학기', value: '2' }
  ];

  const [moduleFilter, setModuleFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState([]);
  const [amountFilter, setAmountFilter] = useState([]);

  const [searchType, setSearchType] = useState('');

  const [studentNumberQuery, setStudentNumberQuery] = useState('');
  const [studentNameQuery, setStudentNameQuery] = useState('');
  const [moduleNameQuery, setModuleNameQuery] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());

  const handleRefreshExpired = () => {
    navigate('/adminLogin');
  };

  const http = new Http({ onRefreshExpired: handleRefreshExpired, onLogout: handleLogout });

  // 검색 목록 변경 시 상태 업데이트
  const handleSearchChange = (event) => {
    const value = event.target.value;
    switch (searchType) {
      case 10:
        setStudentNameQuery(value);
        break;
      case 20:
        setStudentNumberQuery(value);
        break;
      case 30:
        setModuleNameQuery(value);
        break;
      default:
        return;
    }
  };

  // 정렬 기능: 선택된 컬럼에 대해 정렬순서 토글
  const handleSort = (column) => {
    const field = apiFieldMapping[column];
    const isDesc = sortConfig.field === field && sortConfig.direction === 'desc';
    setSortConfig({ field, direction: isDesc ? 'asc' : 'desc' });
  };

  const handleModuleChange = (event) => setModuleFilterSelected(event.target.value);
  const handleYearChange = (event) => setYearFilterSelected(event.target.value);
  const handleAmountChange = (event) => setAmountFilterSelected(event.target.value);
  const handleSemesterChange = (event) => setSemesterFilterSelected(event.target.value);

  const handleTypeChange = (event) => {
    setSearchType(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage - 1);
    }
  };

  // 모듈 필터링 목록 가져오기
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setModuleFilter([
          { id: '이공계', moduleName: '이공계' },
          { id: '비이공계', moduleName: '비이공계' },
          { id: '로봇기구', moduleName: '로봇기구' },
          { id: '로봇전장', moduleName: '로봇전장' },
          { id: '로봇제어', moduleName: '로봇제어' },
          { id: '로봇지능', moduleName: '로봇지능' },
          { id: '융합-로봇마케팅', moduleName: '융합-로봇마케팅' },
          { id: '융합-로봇미디어커뮤니케이션', moduleName: '융합-로봇미디어커뮤니케이션' },
          { id: '내비게이션', moduleName: '내비게이션' },
          { id: '로봇SI', moduleName: '로봇SI' },
          { id: '매니퓰레이션', moduleName: '매니퓰레이션' },
          { id: '인간로봇상호작용', moduleName: '인간로봇상호작용' },
          { id: 'AI시스템', moduleName: 'AI시스템' },
          { id: 'HW시스템', moduleName: 'HW시스템' }
        ]);
        setYearFilter([2020, 2021, 2022, 2023, 2024, 2025]);

        setAmountFilter(['ZERO', 'A', 'S', 'SS']);
        // const response = await http.getWithToken('/api/admin/scholarships/filter');
        // if (response.status === 200 && response.data.data.length > 0) {
        //   const filters = response.data.data[0];

        // } else {
        //   console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
        // }
      } catch (error) {
        console.error('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
      }
    };

    fetchModules();
  }, []);

  // 장학증서 리스트 불러오기
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let url = `/api/admin/scholarships/confirmed?page=${page}&size=${rowsPerPage}`;

        // 필터가 선택된 경우에만 쿼리 파라미터 추가
        if (moduleFilterSelected) {
          url += `&moduleName=${moduleFilterSelected}`;
        }

        if (yearFilterSelected) {
          url += `&year=${yearFilterSelected}`;
        }

        if (semesterFilterSelected) {
          url += `&semester=${semesterFilterSelected}`;
        }

        if (amountFilterSelected) {
          url += `&scholarshipAmount=${amountFilterSelected}`;
        }

        if (sortConfig.field) {
          url += `&sort=${sortConfig.field},${sortConfig.direction}`;
        }

        const response = await http.getWithToken(url);

        setRows(response.data.result.content);
        setTotalPages(response.data.result.totalPages);
      } catch (error) {
        alert('데이터 불러오는데 실패하였습니다. 잠시 후 다시 시도해주세요.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    page,
    rowsPerPage,
    moduleFilterSelected,
    yearFilterSelected,
    semesterFilterSelected,
    amountFilterSelected,
    sortConfig
  ]);

  // 검색 실행 함수 추가
  const handleSearch = async () => {
    if (!searchType) return; // 검색 유형이 선택되지 않았으면 리턴

    setLoading(true);
    try {
      let url = `/api/admin/scholarships/confirmed?page=0&size=${rowsPerPage}`; // 검색 시 첫 페이지로 이동
      setPage(0);

      // 필터가 선택된 경우에만 쿼리 파라미터 추가
      if (moduleFilterSelected) {
        url += `&moduleName=${moduleFilterSelected}`;
      }

      if (yearFilterSelected) {
        url += `&year=${yearFilterSelected}`;
      }

      if (semesterFilterSelected) {
        url += `&semester=${semesterFilterSelected}`;
      }

      if (amountFilterSelected) {
        url += `&scholarshipAmount=${amountFilterSelected}`;
      }

      if (sortConfig.field) {
        url += `&sort=${sortConfig.field},${sortConfig.direction}`;
      }

      // 검색 유형별 url 파라미터 추가
      switch (searchType) {
        case 10:
          if (studentNameQuery) {
            url += `&searchType=name&keyword=${studentNameQuery}`;
          }
          break;
        case 20:
          if (studentNumberQuery) {
            url += `&searchType=studentNumber&keyword=${studentNumberQuery}`;
          }
          break;
        case 30:
          if (moduleNameQuery) {
            url += `&searchType=moduleName&keyword=${moduleNameQuery}`;
          }
          break;
        default:
          break;
      }

      const response = await http.getWithToken(url);

      setRows(response.data.result.content);
      setTotalPages(response.data.result.totalPages);
    } catch (error) {
      alert('검색 중 오류가 발생했습니다. 다시 시도해주세요');
    } finally {
      setLoading(false);
    }
  };

  // 검색 초기화 함수 추가
  const handleSearchReset = async () => {
    setSearchType('');
    setStudentNameQuery('');
    setStudentNumberQuery('');
    setModuleNameQuery('');
    setPage(0);

    // 초기 데이터 다시 로드
    setLoading(true);
    try {
      let url = `/api/admin/scholarships/confirmed?page=0&size=${rowsPerPage}`;

      // 필터가 선택된 경우에만 쿼리 파라미터 추가
      if (moduleFilterSelected) {
        url += `&moduleName=${moduleFilterSelected}`;
      }

      if (yearFilterSelected) {
        url += `&year=${yearFilterSelected}`;
      }

      if (semesterFilterSelected) {
        url += `&semester=${semesterFilterSelected}`;
      }

      if (amountFilterSelected) {
        url += `&scholarshipAmount=${amountFilterSelected}`;
      }

      if (sortConfig.field) {
        url += `&sort=${sortConfig.field},${sortConfig.direction}`;
      }

      const response = await http.getWithToken(url);

      setRows(response.data.result.content);
      setTotalPages(response.data.result.totalPages);
    } catch (error) {
      console.log('데이터를 불러오는데 오류가 발생했습니다. 다시 시도해주세요');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const response = await http.getWithToken('/api/admin/scholarships/confirmed/excel', {}, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('download', 'module_scholarships.xlsx');
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    } catch (err) {
      alert('다운로드 중 오류가 발생했습니다. 잠시 후에 다시 시도해주세요');
    }
  };

  // PDF 다운로드 함수
  const handleDownloadScholarshipPdf = async (row) => {
    try {
      const response = await http.postWithToken(
        `/api/admin/scholarships/${row.id}/pdf`,
        {},
        { responseType: 'blob' }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '장학증서 다운로드', 'height=1000, width=800');
    } catch (error) {
      alert('장학증서 PDF 다운로드에 실패하였습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  // 검색에 모듈 검색 기능을 추가함으로써 조건문이 추가되어 외부로 꺼냄
  let inputValue = '';
  switch (searchType) {
    case 10:
      inputValue = studentNameQuery;
      break;
    case 20:
      inputValue = studentNumberQuery;
      break;
    case 30:
      inputValue = moduleNameQuery;
      break;
    default:
      inputValue = '';
      break;
  }

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" sx={{ flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', width: '95%' }}>
          <Box sx={{ width: '70%', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Box sx={{ width: '13%' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.2 }} id="demo-simple-select-label">
                  검색 유형
                </InputLabel>
                <Select value={searchType} label="Age" onChange={handleTypeChange} disabled={loading}>
                  <MenuItem value="">선택하지 않음</MenuItem>
                  <MenuItem value={10}>이름</MenuItem>
                  <MenuItem value={20}>학번</MenuItem>
                  <MenuItem value={30}>모듈명</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="검색하기…"
                  inputProps={{ 'aria-label': 'search' }}
                  value={inputValue}
                  onChange={handleSearchChange}
                  disabled={loading}
                />
              </Search>
            </Box>
            <Box sx={{ marginLeft: '8px', display: 'flex', gap: '8px' }}>
              <Button
                onClick={handleSearch}
                variant="contained"
                disabled={loading || !searchType}
                size="small"
              >
                검색
              </Button>
              <Button
                onClick={handleSearchReset}
                variant="outlined"
                disabled={loading}
                size="small"
              >
                초기화
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              width: '45%',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <Box sx={{ width: '45%', marginRight: '16px' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="module-filter-label">
                  모듈
                </InputLabel>
                <Select value={moduleFilterSelected} label="Module" onChange={handleModuleChange} disabled={loading}>
                  <MenuItem value="">선택하지 않음</MenuItem>
                  {moduleFilter.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      {module.moduleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '45%', marginRight: '16px' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="year-filter-label">
                  년도
                </InputLabel>
                <Select value={yearFilterSelected} label="Year" onChange={handleYearChange} disabled={loading}>
                  <MenuItem value="">선택하지 않음</MenuItem>
                  {yearFilter.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '45%', marginRight: '16px' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="semester-select-label">
                  학기
                </InputLabel>
                <Select
                  labelId="semester-select-label"
                  id="semester-select"
                  value={semesterFilterSelected}
                  label="학기"
                  onChange={handleSemesterChange}
                  disabled={loading}
                >
                  <MenuItem value="">선택하지않음</MenuItem>
                  {semesterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '65%' }} style={{ marginRight: '10px' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ paddingY: 0.25 }} id="amount-filter-label">
                  장학금액
                </InputLabel>
                <Select value={amountFilterSelected} label="Amount" onChange={handleAmountChange} disabled={loading}>
                  <MenuItem value="">선택하지 않음</MenuItem>
                  {amountFilter.map((amount) => (
                    <MenuItem key={amount} value={amount}>
                      {amount}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', width: '90%' }}>
              <Button onClick={handleExportToExcel} variant="contained" disabled={loading}>
                엑셀추출
              </Button>
            </Box>
          </Box>
        </Box>
        <Paper sx={{ width: '95%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 1200, overflowX: 'hidden' }}>
            <Table stickyHeader aria-label="sticky table" sx={{ fontSize: '0.7rem', tableLayout: 'fixed', width: '100%' }}>
              <TableHead sx={{ backgroundColor: '#bfbfbf' }}>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        cursor: loading ? 'default' : 'pointer',
                        opacity: loading ? 0.6 : 1,
                        ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width })
                      }}
                      onClick={loading ? undefined : () => handleSort(column.id)}
                    >
                      {column.label}
                      {!loading && sortConfig.field === apiFieldMapping[column.id] ? (sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽') : ''}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ textAlign: 'center' }} colSpan={columns.length}>
                      현재 목록이 비어있습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={`${row.studentNumber}-${row.moduleName}-${row.year}-${row.semester}`}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          if (column.id === 'level') { // level로 변경
                            let course;
                            if (value === 'LOW') {
                              course = '초급';
                            } else if (value === 'MIDDLE') {
                              course = '중급';
                            } else if (value === 'HIGH') {
                              course = '고급';
                            } else {
                              course = value;
                            }
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{ fontSize: '0.7rem', ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width }) }}
                              >
                                {course}
                              </TableCell>
                            );
                          } else if (column.id === 'amount') {
                            return (
                              <TableCell key={column.id} align={column.align} sx={{ fontSize: '0.7rem' }}>
                                {value !== null ? `${value.toLocaleString()} 원` : ''}
                              </TableCell>
                            );
                          } else if (column.id === 'studentNumber' || column.id === 'studentName') {
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  fontSize: '0.7rem',
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => openStudentInfoInNewTab(row.studentNumber)}
                              >
                                {value}
                              </TableCell>
                            );
                          } else if (column.id === 'pdf') {
                            return (
                              <TableCell key={column.id} align={column.align} sx={{ fontSize: '0.7rem' }}>
                                {row.amount > 0 && (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleDownloadScholarshipPdf(row)}
                                    size="small"
                                  >
                                    PDF
                                  </Button>
                                )}
                              </TableCell>
                            );
                          } else {
                            const isLongText = ['departmentName', 'scholarshipIssuedNumber', 'moduleName'].includes(column.id);
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  fontSize: '0.7rem',
                                  ...(column.width && { width: column.width, minWidth: column.width, maxWidth: column.width }),
                                  ...(isLongText && {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 0,
                                    whiteSpace: 'nowrap'
                                  })
                                }}
                                title={isLongText ? value : undefined}
                              >
                                {value}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <CustomPagination count={totalPages} page={page} onChange={handleChangePage} disabled={loading} />
      </Box>
    </>
  );
}
